import { useQuery } from '@tanstack/react-query';

interface CoinGeckoResponse {
  bitcoin: {
    usd: number;
  };
}

const CACHE_KEY = 'btc_price_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CachedPrice {
  price: number;
  timestamp: number;
}

function getCachedPrice(): CachedPrice | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const data = JSON.parse(cached) as CachedPrice;
      if (Date.now() - data.timestamp < CACHE_DURATION) {
        return data;
      }
    }
  } catch {
    // Ignore cache errors
  }
  return null;
}

function setCachedPrice(price: number): void {
  try {
    const data: CachedPrice = { price, timestamp: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // Ignore cache errors
  }
}

async function fetchBitcoinPrice(): Promise<number> {
  // Check localStorage cache first
  const cached = getCachedPrice();
  if (cached) {
    return cached.price;
  }

  const response = await fetch(
    'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd',
    {
      headers: {
        'Accept': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch Bitcoin price');
  }

  const data: CoinGeckoResponse = await response.json();
  const price = data.bitcoin.usd;

  // Cache the result
  setCachedPrice(price);

  return price;
}

export function useBitcoinPrice() {
  return useQuery({
    queryKey: ['bitcoin-price'],
    queryFn: fetchBitcoinPrice,
    staleTime: CACHE_DURATION, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: CACHE_DURATION, // Refetch every 5 minutes
    retry: 2,
    retryDelay: 1000,
    placeholderData: () => {
      // Use cached price as placeholder if available
      const cached = getCachedPrice();
      return cached?.price;
    },
  });
}

/**
 * Convert USD to sats based on current BTC price
 * @param usd - Amount in USD
 * @param btcPrice - Current BTC price in USD
 * @returns Amount in satoshis
 */
export function usdToSats(usd: number, btcPrice: number): number {
  if (!btcPrice || btcPrice <= 0) return 0;
  // 1 BTC = 100,000,000 sats
  const btcAmount = usd / btcPrice;
  return Math.round(btcAmount * 100_000_000);
}

/**
 * Format sats with thousands separator
 */
export function formatSats(sats: number): string {
  return sats.toLocaleString();
}
